/**
 * Device Templates - Pre-configured device specifications
 * Select a template to auto-fill hardware, software, and media information
 */

export interface DeviceTemplate {
  id: string;
  name: string;
  deviceType: string;
  description: string;
  hardware: {
    manufacturer: string;
    model: string;
    firmwareVersion?: string;
    processor?: string;
    memory?: string;
    storage?: string;
  };
  software?: {
    osType?: string;
    osVersion?: string;
  };
  media?: {
    primaryImageUrl?: string;
  };
}

export const deviceTemplates: DeviceTemplate[] = [
  // POS Devices
  {
    id: 'elo-pos-15',
    name: 'Elo I-Series 15" POS Terminal',
    deviceType: 'pos',
    description: 'Elo I-Series 3.0 15-inch all-in-one touchscreen POS terminal with Android',
    hardware: {
      manufacturer: 'Elo Touch Solutions',
      model: 'I-Series 3.0 (15-inch)',
      firmwareVersion: '3.0.1',
      processor: 'Qualcomm Snapdragon 845',
      memory: '4 GB RAM',
      storage: '64 GB eMMC',
    },
    software: {
      osType: 'Android',
      osVersion: '10',
    },
    media: {
      primaryImageUrl: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400',
    },
  },
  {
    id: 'clover-station-pro',
    name: 'Clover Station Pro 3.0',
    deviceType: 'pos',
    description: 'Clover Station Pro all-in-one POS system with 14-inch display',
    hardware: {
      manufacturer: 'Clover',
      model: 'Station Pro 3.0',
      firmwareVersion: '12.5.2',
      processor: 'Octa-core ARM',
      memory: '8 GB RAM',
      storage: '128 GB SSD',
    },
    software: {
      osType: 'Android',
      osVersion: '11',
    },
    media: {
      primaryImageUrl: 'https://images.unsplash.com/photo-1556742111-a301076d9d18?w=400',
    },
  },
  
  // Mobile POS
  {
    id: 'ipad-pos',
    name: 'iPad Pro 12.9" Mobile POS',
    deviceType: 'mobile',
    description: 'iPad Pro configured as mobile POS with card reader',
    hardware: {
      manufacturer: 'Apple',
      model: 'iPad Pro 12.9" (6th Gen)',
      firmwareVersion: 'iOS 17.4',
      processor: 'M2 Chip',
      memory: '8 GB RAM',
      storage: '256 GB',
    },
    software: {
      osType: 'iOS',
      osVersion: '17.4',
    },
    media: {
      primaryImageUrl: 'https://images.unsplash.com/photo-1544244015-0df4b3ffc6b0?w=400',
    },
  },
  
  // Kiosk
  {
    id: 'elo-kiosk-22',
    name: 'Elo I-Series 22" Self-Service Kiosk',
    deviceType: 'kiosk',
    description: 'Elo I-Series 22-inch self-service kiosk with integrated payment',
    hardware: {
      manufacturer: 'Elo Touch Solutions',
      model: 'I-Series 4.0 Kiosk (22-inch)',
      firmwareVersion: '4.0.2',
      processor: 'Intel Core i5',
      memory: '8 GB RAM',
      storage: '256 GB SSD',
    },
    software: {
      osType: 'Windows',
      osVersion: '10 IoT Enterprise',
    },
    media: {
      primaryImageUrl: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=400',
    },
  },
  
  // Printer
  {
    id: 'epson-tm-m30',
    name: 'Epson TM-m30 Receipt Printer',
    deviceType: 'printer',
    description: 'Epson TM-m30 3-inch thermal receipt printer with Bluetooth and Ethernet',
    hardware: {
      manufacturer: 'Epson',
      model: 'TM-m30',
      firmwareVersion: 'v2.5',
      processor: 'N/A',
      memory: 'N/A',
      storage: 'N/A',
    },
    software: {
      osType: 'Embedded',
      osVersion: 'Firmware 2.5',
    },
    media: {
      primaryImageUrl: 'https://images.unsplash.com/photo-1612815154858-60aa4c59eaa6?w=400',
    },
  },
  
  // Payment Device
  {
    id: 'pax-a920',
    name: 'PAX A920 Payment Terminal',
    deviceType: 'payment',
    description: 'PAX A920 Pro Android smart payment terminal with EMV, NFC, and contactless',
    hardware: {
      manufacturer: 'PAX Technology',
      model: 'A920 Pro',
      firmwareVersion: 'A920-ARG-v7.1.5',
      processor: 'ARM Cortex A53 Quad-core',
      memory: '2 GB RAM',
      storage: '16 GB',
    },
    software: {
      osType: 'Android',
      osVersion: '8.1',
    },
    media: {
      primaryImageUrl: 'https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400',
    },
  },
];

/**
 * Get template by ID
 */
export const getDeviceTemplate = (templateId: string): DeviceTemplate | undefined => {
  return deviceTemplates.find(t => t.id === templateId);
};

/**
 * Get templates by device type
 */
export const getTemplatesByType = (deviceType: string): DeviceTemplate[] => {
  return deviceTemplates.filter(t => t.deviceType === deviceType);
};

/**
 * Get all template choices for select input
 */
export const getTemplateChoices = () => {
  return deviceTemplates.map(t => ({
    id: t.id,
    name: `${t.name} (${t.hardware.manufacturer})`,
  }));
};

